import { createContext, useContext, useReducer, useEffect } from 'react';
import api from '../api/axios';

const AuthContext = createContext({
  user: null, token: null, loading: true, error: null, permissions: {},
});

const reducer = (state, action) => {
  switch (action.type) {
    case 'AUTH_START':   return { ...state, loading: true, error: null };
    case 'AUTH_SUCCESS': return { ...state, loading: false, user: action.payload.user, token: action.payload.token, permissions: action.payload.permissions || {}, error: null };
    case 'AUTH_FAIL':    return { ...state, loading: false, error: action.payload };
    case 'LOGOUT':       return { user: null, token: null, permissions: {}, loading: false, error: null };
    case 'CLEAR_ERROR':  return { ...state, error: null };
    default:             return state;
  }
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, {
    user: null, token: null, loading: true, error: null, permissions: {},
  });

  // Restore session on mount
  useEffect(() => {
    const token       = localStorage.getItem('cs_token');
    const user        = JSON.parse(localStorage.getItem('cs_user')        || 'null');
    const permissions = JSON.parse(localStorage.getItem('cs_permissions') || 'null');

    if (token && user) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      if (permissions) {
        // Use stored permissions (set on login, cleared on logout)
        dispatch({ type: 'AUTH_SUCCESS', payload: { user, token, permissions } });
      } else if (user.role === 'owner') {
        // Owner always gets full permissions
        dispatch({ type: 'AUTH_SUCCESS', payload: { user, token, permissions: getOwnerPermissions() } });
      } else {
        // Fetch fresh from server as fallback
        api.get('/users/roles')
          .then(({ data }) => {
            const role = (data.roles || []).find(r => r.name === user.role);
            const perms = role ? JSON.parse(JSON.stringify(role.permissions)) : {};
            localStorage.setItem('cs_permissions', JSON.stringify(perms));
            dispatch({ type: 'AUTH_SUCCESS', payload: { user, token, permissions: perms } });
          })
          .catch(() => {
            dispatch({ type: 'AUTH_SUCCESS', payload: { user, token, permissions: {} } });
          });
      }
    } else {
      dispatch({ type: 'AUTH_FAIL', payload: null });
    }
  }, []);

  // ── Login ──
  const login = async (email, password, location = '') => {
    dispatch({ type: 'AUTH_START' });
    try {
      const { data } = await api.post('/auth/login', { email, password });
      // Permissions come directly from server on login response
      const permissions = data.user.permissions
        ? JSON.parse(JSON.stringify(data.user.permissions))
        : {};

      localStorage.setItem('cs_token',       data.token);
      const userWithLocation = { ...data.user, selectedLocation: location };
      localStorage.setItem('cs_user',        JSON.stringify(userWithLocation));
      localStorage.setItem('cs_permissions', JSON.stringify(permissions));

      api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
      dispatch({ type: 'AUTH_SUCCESS', payload: { user: { ...data.user, selectedLocation: location }, token: data.token, permissions } });
      return { success: true, role: data.user.role };
    } catch (err) {
      const msg = err.response?.data?.message || 'Invalid email or password';
      dispatch({ type: 'AUTH_FAIL', payload: msg });
      return { success: false, error: msg };
    }
  };

  // ── Logout — clear EVERYTHING ──
  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('cs_token');
    localStorage.removeItem('cs_user');
    localStorage.removeItem('cs_permissions');
    delete api.defaults.headers.common['Authorization'];
    dispatch({ type: 'LOGOUT' });
  };

  const clearError = () => dispatch({ type: 'CLEAR_ERROR' });

  // ── Permission helper — owner always gets full access ──
  const can = (permission) => {
    if (state.user?.role === 'owner') return true;
    return !!state.permissions?.[permission];
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout, clearError, can }}>
      {children}
    </AuthContext.Provider>
  );
}

// Owner always has all permissions
const getOwnerPermissions = () => ({
  canViewJobs:true, canCreateJobs:true, canEditJobs:true, canDeleteJobs:true,
  canUploadExcel:true, canDownloadExcel:true, canViewRevenue:true, canSearchJobs:true,
  canViewInspections:true, canCreateInspections:true, canEditInspections:true,
  canDeleteInspections:true, canReviewInspections:true,
  canViewStaff:true, canManageStaff:true, canViewReports:true, canViewActivity:true,
  canManageSettings:true,
});

export const useAuth = () => useContext(AuthContext);
